import * as React from "react";
import {cn} from "@/lib/utils";

interface InputProps extends React.ComponentProps<"input"> {
   icon?: React.ReactNode;
   iconPosition?: "left" | "right";
}

/** Native date/time/datetime-local use internal shadow controls; `display:flex` on the input breaks picker indicator placement (e.g. icons jump left). */
function isNativeTemporalInputType(t: string | undefined): boolean {
   return t === "date" || t === "time" || t === "datetime-local";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
   ({className, type, icon, iconPosition = "left", ...props}, ref) => {
      const hasIcon = !!icon;
      const isTemporal = isNativeTemporalInputType(type);
      return (
         <div
            className={cn(
               "relative",
               "flex",
               "h-[42px]",
               "items-center",
               "w-full",
               "overflow-hidden",
               "rounded-full",
               "leading-6",
               "border",
               "border-gray-300",
               "dark:border-gray-600",
               "!bg-white",
               "dark:!bg-gray_comp",
               "ring-gray-600",
               "shadow-sm",
               "transition-colors",
               "focus-within:ring-1",
               "focus-within:ring-ring",
               "disabled:cursor-not-allowed",
               "disabled:opacity-50",
               hasIcon ? "pl-2" : "",
               // Native picker appearance + contrast in light/dark (calendar/clock indicators)
               isTemporal && "[color-scheme:light] dark:[color-scheme:dark]",
               className
            )}
         >
            {icon && iconPosition === "left" && (
               <span
                  className={cn(
                     "pointer-events-none",
                     "flex",
                     "items-center",
                     "text-muted-foreground",
                     "mr-2"
                  )}
               >
                  {icon}
               </span>
            )}
            <input
               type={type}
               dir={isTemporal ? "ltr" : undefined}
               className={cn(
                  isTemporal
                     ? [
                          "block",
                          "min-w-0",
                          "flex-1",
                          "self-stretch",
                          "rounded-[inherit]",
                          "text-foreground",
                          "[color-scheme:inherit]",
                       ]
                     : ["flex", "w-full"],
                  "bg-transparent",
                  "px-3",
                  "py-1",
                  "text-base",
                  "transition-colors",
                  "file:border-0",
                  "file:bg-transparent",
                  "file:text-sm",
                  "file:font-medium",
                  "file:text-foreground",
                  "placeholder:text-muted-foreground",
                  "focus-visible:outline-none",
                  "focus-visible:ring-0",
                  "focus-visible:ring-transparent",
                  "focus-visible:border-none",
                  "border-none",
                  "ring-0",
                  "outline-none",
                  "shadow-none",
                  "md:text-sm",
                  hasIcon && iconPosition === "left" ? "pl-0" : "",
                  hasIcon && iconPosition === "right" ? "pr-0" : ""
               )}
               ref={ref}
               {...props}
            />
            {icon && iconPosition === "right" && (
               <span
                  className={cn(
                     "pointer-events-none",
                     "flex",
                     "items-center",
                     "text-muted-foreground",
                     "ml-2"
                  )}
               >
                  {icon}
               </span>
            )}
         </div>
      );
   }
);
Input.displayName = "Input";

export {Input};
